import { Fragment, ReactNode, useMemo } from 'react';
import { useLang } from 'hooks/lang';

import { User } from 'entities';

import { Skeleton, Column, Row, IconButton, Collapse, Tag, Card } from 'components/Blocks';
import { UserAvatar } from 'components/UserAvatar';

import { ChevronRightIcon, OpenFolderIcon } from 'components/Icons';

import { useMultiStateV2 } from 'hooks';

import { compareByName, sortByProperty } from 'utils/array';

import { GetLabelFn } from 'types';
import { cn } from 'styles';

interface BaseEntry {
  status: string;
  owner: User;
  id: string;
}

type UserRecruitmentControl<E extends BaseEntry> = {
  owner: User;
  userEntries: Record<string, E[]>;
  total: number;
};

interface BreakdownProps<E extends BaseEntry> {
  loading?: boolean;
  title?: string;
  entries: E[];
  statusConfig: Record<string, { color: string; code: string; getLabel: GetLabelFn }>;
  render: (e: E) => ReactNode;
  totalResolver?: (userId: string) => number;
}

export function UserOwnershipBreakdown<E extends BaseEntry>({
  entries,
  loading,
  title,
  statusConfig,
  render,
  totalResolver,
}: BreakdownProps<E>): JSX.Element {
  const { t } = useLang();

  const [openedUsers, { dispatch }] = useMultiStateV2<Record<string, boolean>>({});

  const projectsByUser = useMemo(() => {
    const mapping = entries.reduce(
      (acc, entry) => {
        const { userEntries, total } = acc[entry.owner.id] ?? {};

        return {
          ...acc,

          [entry.owner.id]: {
            owner: entry.owner,

            total: (total ?? 0) + 1,

            userEntries: {
              ...userEntries,

              [entry.status]: [...(userEntries?.[entry.status] || []), entry],
            },
          },
        } as Record<string, UserRecruitmentControl<E>>;
      },
      {} as Record<string, UserRecruitmentControl<E>>,
    );

    return sortByProperty(Object.values(mapping), ['owner'], {
      owner: compareByName,
    });
  }, [entries]);

  return (
    <Column>
      <p className="flex items-center gap-2 font-medium">
        <OpenFolderIcon size={20} />
        {title}
      </p>

      {loading ? (
        <Skeleton className="h-[80%]" />
      ) : (
        <Card flow="column" className="scrollbar max-h-[750px] overflow-y-auto pr-6" gap="2">
          {!entries.length ? (
            <p className="text-center">{t('no-active-ownership')}</p>
          ) : (
            projectsByUser.map(({ owner, userEntries, total }) => (
              <Column key={owner.id} gap="0" relative>
                <Row className="sticky -top-4 z-[2] bg-background p-2" gap="2">
                  <IconButton
                    size="small"
                    onClick={() => dispatch({ [owner.id]: !openedUsers[owner.id] })}
                  >
                    <ChevronRightIcon
                      size={16}
                      className={cn('transition-transform', { 'rotate-90': openedUsers[owner.id] })}
                    />
                  </IconButton>

                  <UserAvatar user={owner} name size={32} />

                  <p className="ml-auto text-primary">{totalResolver?.(owner.id) ?? total}</p>
                </Row>

                <Collapse expanded={openedUsers[owner.id]} onlyRenderIfExpanded>
                  <Column className="p-6 pl-10" gap="2">
                    {Object.entries(userEntries).map(([status, activeEntries]) => (
                      <Column gap="0" key={`user-project-${owner.id}-${status}`}>
                        <Tag
                          round={false}
                          name={statusConfig[status].getLabel(t)}
                          color={statusConfig[status].color}
                          type="outlined"
                        />

                        {activeEntries.map((entry) => (
                          <Fragment key={`user-project-${entry.id}`}>{render(entry as E)}</Fragment>
                        ))}
                      </Column>
                    ))}
                  </Column>
                </Collapse>
              </Column>
            ))
          )}
        </Card>
      )}
    </Column>
  );
}
